import { useCallback, useState } from 'react';

import { HttpResponse } from '~/services/types';

import asyncPoll from '~/utils/asyncPoll';

export type JobProgressState = 'accepted' | 'processing' | 'failed' | 'completed';

export interface BaseProgressStatus {
  id?: string;
  type: string;
  state: JobProgressState;
  message?: string;
  meta?: Record<string, any>;
}

export interface ProgressPollingState<T extends BaseProgressStatus> {
  isLoading: boolean;
  isComplete: boolean;
  statusMessage: string | null;
  errorMessage: string | null;
  statuses: Array<T>;
  validationErrors: Array<any>; // Replace with your validation error type
}

interface UseProgressPollingProps<T extends BaseProgressStatus, P = any> {
  apiCall: (props: P) => Promise<HttpResponse<{ id: string }>>;
  getStatus: (id: string) => Promise<HttpResponse<Array<T>>>;
  onClose: () => void;
  validateCompletion?: (statuses: Array<T>) => boolean;
  options?: {
    maxTime?: number;
    pollInterval?: number;
  };
}

export function useProgressPolling<T extends BaseProgressStatus, P = any>({
  apiCall,
  getStatus,
  onClose,
  validateCompletion,
  options = {},
}: UseProgressPollingProps<T, P>) {
  const [state, setState] = useState<ProgressPollingState<T>>({
    isLoading: false,
    isComplete: false,
    statusMessage: null,
    errorMessage: null,
    statuses: [],
    validationErrors: [],
  });

  const TWO_MINUTES_IN_MS = options.maxTime ?? 60000 * 2;
  const POLL_RATE_IN_MS = options.pollInterval ?? 500;

  const handleError = useCallback((message: string) => {
    setState((prev) => ({
      ...prev,
      errorMessage: message,
      statusMessage: null,
      isLoading: false,
    }));
  }, []);

  const resetAndClose = useCallback(() => {
    setState({
      isLoading: false,
      isComplete: false,
      statusMessage: null,
      errorMessage: null,
      statuses: [],
      validationErrors: [],
    });
    onClose();
  }, [onClose]);

  const setJobStatuses = useCallback(
    (result: Array<T>) => {
      setState((prev) => ({
        ...prev,
        statuses: result,
      }));

      const failedStatus = result.find((status) => status.state === 'failed');
      if (failedStatus) {
        handleError(failedStatus.message);
      }
    },
    [handleError],
  );

  const pollStatus = useCallback(
    async (id: string) => {
      try {
        await asyncPoll({
          apiCall: () => getStatus(id),
          validateFunction: async ({ result }) => {
            setJobStatuses(result);

            // Use custom validation if provided
            if (validateCompletion) {
              const isComplete = validateCompletion(result);
              if (isComplete) {
                setState((prev) => ({
                  ...prev,
                  isLoading: false,
                  isComplete: true,
                  statusMessage: 'Process completed successfully',
                }));
              }
              return isComplete;
            }

            // Default validation: check if all statuses are completed or if any failed
            const isComplete =
              result.every((status) => status.state === 'completed') ||
              result.some((status) => status.state === 'failed');

            if (isComplete) {
              setState((prev) => ({
                ...prev,
                isLoading: false,
                isComplete: true,
                statusMessage: result.some((status) => status.state === 'failed')
                  ? 'Process failed'
                  : 'Process completed successfully',
              }));
            }

            return isComplete;
          },
          maxTime: TWO_MINUTES_IN_MS,
          pollInterval: POLL_RATE_IN_MS,
        });
      } catch (e) {
        handleError(e.message);
        setState((prev) => ({ ...prev, isLoading: false }));
        return;
      }
    },
    [TWO_MINUTES_IN_MS, POLL_RATE_IN_MS, getStatus, setJobStatuses, validateCompletion, handleError],
  );

  const runStartJob = useCallback(
    async (props?: P) => {
      setState((prev) => ({
        ...prev,
        isLoading: true,
        isComplete: false,
        errorMessage: null,
        validationErrors: [],
      }));

      try {
        const { result } = await apiCall(props);
        if (!result?.id) {
          handleError('Failed to start job');
          return;
        }
        pollStatus(result.id);
      } catch (e) {
        if (e.status === 409) {
          setState((prev) => ({
            ...prev,
            validationErrors: e.errors,
            isLoading: false,
          }));
        } else {
          setState((prev) => ({
            ...prev,
            errorMessage: e.message,
            isLoading: false,
          }));
        }
      }
    },
    [pollStatus, handleError, apiCall],
  );

  return {
    ...state,
    runStartJob,
    resetAndClose,
  };
}
